import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import {
    registrationModeState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';

/**
 * While the registration tool is armed, holding Shift swaps the registration
 * mode between rotation and translation; releasing Shift restores the mode
 * that was active before the key went down. The window losing focus, or the
 * tool being disarmed, counts as a release: the key-up would never arrive,
 * and a swapped mode must not outlive the press that caused it. Independent
 * of which viewport hosts the tool: it reads only the shared tool atoms.
 */
export function useRegistrationModeShiftToggle(): void {
    const selectedTool = useRecoilValue(selectedToolState);
    const setRegistrationOptions = useSetRecoilState(registrationModeState);
    const modeBeforeShift = useRef<RegistrationMode | null>(null);
    const shiftHeld = useRef(false);

    useEffect(() => {
        if (selectedTool !== 'registration') {
            return undefined;
        }
        const release = () => {
            if (!shiftHeld.current) {
                return;
            }
            shiftHeld.current = false;
            setRegistrationOptions((prev) => ({
                ...prev,
                mode: modeBeforeShift.current,
            }));
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Shift' || shiftHeld.current) {
                return;
            }
            shiftHeld.current = true;
            setRegistrationOptions((prev) => {
                modeBeforeShift.current = prev.mode;
                return {
                    ...prev,
                    mode:
                        prev.mode === RegistrationMode.ROTATION
                            ? RegistrationMode.TRANSLATION
                            : RegistrationMode.ROTATION,
                };
            });
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Shift') {
                release();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', release);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', release);
            release();
        };
    }, [setRegistrationOptions, selectedTool]);
}
